import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getUserOrders } from '../../Store/orders/ordersSlice';
import ProfileNavigation from '../Profile/ProfileNavigation';
import { assetUrl } from '../../utils/imageUrl';
import styles from '../../styles/UserOrders.module.scss';

const formatMoney = (value) => `${Number(value || 0).toLocaleString('uk-UA')} ₴`;

const OrderProductRow = ({ item }) => {
  const title = item.title || item.name || 'Товар';
  const quantity = Number(item.quantity) || 1;
  const price = Number(item.price) || 0;
  const lineTotal = quantity * price;
  const image = Array.isArray(item.images) ? item.images[0] : null;

  const content = (
    <>
      <div className={styles.itemImage}>
        {image ? (
          <img src={assetUrl(image)} alt={title} />
        ) : (
          <span className={styles.itemImagePlaceholder}>Немає фото</span>
        )}
      </div>
      <div className={styles.itemBody}>
        <div className={styles.itemName}>{title}</div>
        {item.article && (
          <div className={styles.itemArticle}>Артикул: {item.article}</div>
        )}
      </div>
      <div className={styles.itemQuantity}>{quantity} шт.</div>
      <div className={styles.itemUnitPrice}>{formatMoney(price)}</div>
      <div className={styles.itemLineTotal}>{formatMoney(lineTotal)}</div>
    </>
  );

  if (item.id) {
    return (
      <Link to={`/products/${item.id}`} className={styles.orderItem}>
        {content}
      </Link>
    );
  }

  return <div className={styles.orderItem}>{content}</div>;
};

const UserOrders = () => {
  const dispatch = useDispatch();
  const { currentUser } = useSelector(({ user }) => user);
  const { userOrders, isLoading } = useSelector(({ orders }) => orders);
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    if (currentUser) {
      dispatch(getUserOrders());
    }
  }, [dispatch, currentUser]);

  useEffect(() => {
    document.title = 'Мої замовлення - Інтернет-магазин';
    return () => {
      document.title = 'Інтернет-магазин';
    };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Дату не вказано';
    const date = new Date(dateString);
    return date.toLocaleDateString('uk-UA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleOrderDetails = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  if (!currentUser) {
    return (
      <section className={styles.userOrders}>
        <div className={styles.empty}>
          <h2>Увійдіть в систему</h2>
          <p>Для перегляду замовлень необхідно увійти в систему</p>
          <Link to="/" className={styles.link}>
            Повернутися на головну
          </Link>
        </div>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section className={styles.userOrders}>
        <ProfileNavigation />
        <div className={styles.loading}>Завантаження замовлень...</div>
      </section>
    );
  }

  return (
    <section className={styles.userOrders}>
      <ProfileNavigation />
      <div className={styles.contentWrapper}>
        <div className={styles.header}>
          <h2>Мої замовлення</h2>
          <p className={styles.count}>
            Усього замовлень: <strong>{userOrders.length}</strong>
          </p>
        </div>

        {userOrders.length === 0 ? (
          <div className={styles.empty}>
            <h3>У вас поки немає замовлень</h3>
            <p>Зробіть своє перше замовлення, щоб воно відобразилося тут</p>
            <Link to="/" className={styles.link}>
              Перейти до каталогу
            </Link>
          </div>
        ) : (
          <div className={styles.ordersList}>
            {userOrders.map((order) => (
              <div key={order.id} className={styles.orderCard}>
                <div className={styles.orderHeader} onClick={() => toggleOrderDetails(order.id)}>
                  <div className={styles.orderInfo}>
                    <div className={styles.orderNumber}>
                      <span className={styles.orderNumberLabel}>Замовлення №</span>
                      <span className={styles.orderNumberValue}>{String(order.id).padStart(6, '0')}</span>
                    </div>
                    <div className={styles.orderDate}>
                      {formatDate(order.createdAt)}
                    </div>
                  </div>
                  <div className={styles.orderSummary}>
                    <div className={styles.orderTotal}>
                      Сума: <strong>{formatMoney(order.total)}</strong>
                    </div>
                    <div className={styles.orderStatus}>
                      {order.payment || '—'}
                    </div>
                    <svg
                      className={`${styles.arrowIcon} ${expandedOrder === order.id ? styles.arrowOpen : ''}`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>

                {expandedOrder === order.id && (
                  <div className={styles.orderDetails}>
                    <div className={styles.itemsSection}>
                      <h4 className={styles.itemsTitle}>Товари в замовленні</h4>
                      <div className={styles.itemsTableHead}>
                        <span>Фото</span>
                        <span>Товар</span>
                        <span>К-сть</span>
                        <span>Ціна</span>
                        <span>Сума</span>
                      </div>
                      <div className={styles.itemsList}>
                        {order.items && Array.isArray(order.items) && order.items.length > 0 ? (
                          order.items.map((item, index) => (
                            <OrderProductRow key={`${order.id}-${item.id || index}`} item={item} />
                          ))
                        ) : (
                          <div className={styles.noItems}>Товари не вказано</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default UserOrders;
